import React, { useState, useEffect, useCallback } from 'react';
import API_URL from '../config';

const TOKEN_KEY = 'consultor_token';

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` };
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

const STATUS_LABELS = {
  rascunho: 'Rascunho',
  em_analise: 'Em análise',
  em_acao: 'Em ação',
  verificacao: 'Em verificação',
  encerrada: 'Encerrada',
};

export default function ConsultorPortalPage() {
  const [consultor, setConsultor] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [view, setView] = useState('clientes'); // 'clientes' | 'ncs' | 'nc'
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [ncs, setNcs] = useState([]);
  const [ncDetalhe, setNcDetalhe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    window.location.replace('/portal-consultor/login');
  };

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { logout(); return; }
    try {
      const res = await fetch(`${API_URL}/api/consultor/me`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return; }
      if (!res.ok) throw new Error('Erro ao carregar perfil');
      setConsultor(await res.json());
    } catch (e) {
      setErro(e.message);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadClientes = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const res = await fetch(`${API_URL}/api/consultor/clientes`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return; }
      if (!res.ok) throw new Error('Erro ao carregar clientes');
      setClientes(await res.json());
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    checkAuth();
    loadClientes();
  }, [checkAuth, loadClientes]);

  const abrirNCs = async (cliente) => {
    setClienteSelecionado(cliente);
    setView('ncs');
    setNcs([]);
    setErro('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/consultor/clientes/${cliente.id}/ncs?skip=0&limit=50`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Erro ao carregar NCs');
      const data = await res.json();
      setNcs(data.items || []);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  };

  const abrirNC = async (nc) => {
    setView('nc');
    setNcDetalhe(null);
    setErro('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/consultor/nc/${nc.id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Erro ao carregar NC');
      setNcDetalhe(await res.json());
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  };

  const s = {
    page: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Inter', sans-serif" },
    header: {
      background: '#fff', borderBottom: '1px solid #e2e8f0',
      padding: '0 2rem', height: '64px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    },
    logo: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
    logoIcon: {
      width: 36, height: 36, borderRadius: '8px',
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    },
    logoText: { fontWeight: 700, fontSize: '1rem', color: '#1e293b' },
    logoPre: { fontWeight: 400, fontSize: '0.75rem', color: '#64748b', display: 'block' },
    headerRight: { display: 'flex', alignItems: 'center', gap: '1rem' },
    consultorBadge: {
      fontSize: '0.85rem', color: '#475569',
      background: '#f1f5f9', borderRadius: '20px', padding: '0.3rem 0.75rem',
    },
    logoutBtn: {
      background: 'none', border: '1px solid #e2e8f0', borderRadius: '8px',
      padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem',
      color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.4rem',
    },
    main: { maxWidth: '960px', margin: '0 auto', padding: '2rem 1.5rem' },
    breadcrumb: {
      display: 'flex', alignItems: 'center', gap: '0.5rem',
      fontSize: '0.85rem', color: '#64748b', marginBottom: '1.5rem',
    },
    breadLink: {
      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
      color: '#667eea', fontWeight: 500, fontSize: '0.85rem',
    },
    card: {
      background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
      padding: '1.25rem 1.5rem', marginBottom: '0.75rem',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      cursor: 'pointer', transition: 'box-shadow .15s',
    },
    cardTitle: { fontWeight: 600, color: '#1e293b', fontSize: '0.95rem' },
    cardSub: { color: '#64748b', fontSize: '0.8rem', marginTop: '0.2rem' },
    sectionTitle: { fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', marginBottom: '1rem' },
    badge: (status) => {
      const colors = {
        rascunho: '#94a3b8', em_analise: '#f59e0b', em_acao: '#3b82f6',
        verificacao: '#8b5cf6', encerrada: '#10b981',
      };
      return {
        display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px',
        fontSize: '0.75rem', fontWeight: 600, color: '#fff',
        background: colors[status] || '#94a3b8',
      };
    },
    emptyState: {
      textAlign: 'center', padding: '3rem 1rem', color: '#94a3b8',
    },
    detailBox: {
      background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
      padding: '1.5rem', marginBottom: '1rem',
    },
    detailLabel: { fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' },
    detailValue: { color: '#1e293b', fontSize: '0.925rem', lineHeight: 1.5 },
    row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
    chevron: { color: '#cbd5e1', fontSize: '20px' },
  };

  const Header = () => (
    <header style={s.header}>
      <div style={s.logo}>
        <div style={s.logoIcon}>
          <span className="material-symbols-outlined" style={{ color: '#fff', fontSize: 20 }}>handshake</span>
        </div>
        <div>
          <span style={s.logoPre}>Portal do Consultor</span>
          <span style={s.logoText}>Qaa.sa</span>
        </div>
      </div>
      <div style={s.headerRight}>
        {consultor && (
          <span style={s.consultorBadge}>{consultor.nome || consultor.codigo}</span>
        )}
        <button style={s.logoutBtn} onClick={logout}>
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>logout</span>
          Sair
        </button>
      </div>
    </header>
  );

  const ErroMsg = () => erro ? (
    <div style={{
      padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca',
      borderRadius: '8px', color: '#dc2626', fontSize: '0.875rem',
      display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem',
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>error</span>
      {erro}
    </div>
  ) : null;

  if (view === 'clientes') {
    return (
      <div style={s.page}>
        <Header />
        <div style={s.main}>
          <h2 style={s.sectionTitle}>Seus clientes</h2>
          <ErroMsg />
          {loading ? (
            <div style={s.emptyState}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, display: 'block', marginBottom: '0.5rem' }}>sync</span>
              Carregando...
            </div>
          ) : clientes.length === 0 ? (
            <div style={s.emptyState}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, display: 'block', marginBottom: '0.5rem' }}>business</span>
              Nenhum cliente associado ao seu perfil de consultor.
            </div>
          ) : clientes.map((c) => (
            <div key={c.id} style={s.card} onClick={() => abrirNCs(c)}>
              <div>
                <div style={s.cardTitle}>{c.nome}</div>
                {c.industria && <div style={s.cardSub}>{c.industria}</div>}
              </div>
              <span className="material-symbols-outlined" style={s.chevron}>chevron_right</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (view === 'ncs') {
    return (
      <div style={s.page}>
        <Header />
        <div style={s.main}>
          <nav style={s.breadcrumb}>
            <button style={s.breadLink} onClick={() => setView('clientes')}>Clientes</button>
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_right</span>
            <span style={{ color: '#1e293b' }}>{clienteSelecionado?.nome}</span>
          </nav>
          <h2 style={s.sectionTitle}>Não-Conformidades</h2>
          <ErroMsg />
          {loading ? (
            <div style={s.emptyState}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, display: 'block', marginBottom: '0.5rem' }}>sync</span>
              Carregando...
            </div>
          ) : ncs.length === 0 ? (
            <div style={s.emptyState}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, display: 'block', marginBottom: '0.5rem' }}>task_alt</span>
              Nenhuma NC disponível para este cliente.
            </div>
          ) : ncs.map((nc) => (
            <div key={nc.id} style={s.card} onClick={() => abrirNC(nc)}>
              <div>
                <div style={s.cardTitle}>
                  {nc.numero_nc ? `#${nc.numero_nc}` : `NC ${nc.id}`}
                  {nc.descricao_nc && ` — ${nc.descricao_nc.slice(0, 60)}${nc.descricao_nc.length > 60 ? '…' : ''}`}
                </div>
                <div style={s.cardSub}>
                  <span style={s.badge(nc.workflow_status)}>{STATUS_LABELS[nc.workflow_status] || nc.workflow_status}</span>
                  {nc.created_at && <span style={{ marginLeft: '0.5rem' }}>{formatDate(nc.created_at)}</span>}
                </div>
              </div>
              <span className="material-symbols-outlined" style={s.chevron}>chevron_right</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // view === 'nc'
  return (
    <div style={s.page}>
      <Header />
      <div style={s.main}>
        <nav style={s.breadcrumb}>
          <button style={s.breadLink} onClick={() => setView('clientes')}>Clientes</button>
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_right</span>
          <button style={s.breadLink} onClick={() => setView('ncs')}>{clienteSelecionado?.nome}</button>
          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_right</span>
          <span style={{ color: '#1e293b' }}>
            {ncDetalhe ? (ncDetalhe.numero_nc ? `#${ncDetalhe.numero_nc}` : `NC ${ncDetalhe.id}`) : 'Carregando...'}
          </span>
        </nav>

        <ErroMsg />

        {loading ? (
          <div style={s.emptyState}>
            <span className="material-symbols-outlined" style={{ fontSize: 40, display: 'block', marginBottom: '0.5rem' }}>sync</span>
            Carregando...
          </div>
        ) : ncDetalhe ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <h2 style={{ ...s.sectionTitle, margin: 0 }}>
                {ncDetalhe.numero_nc ? `#${ncDetalhe.numero_nc}` : `NC ${ncDetalhe.id}`}
              </h2>
              <span style={s.badge(ncDetalhe.workflow_status)}>
                {STATUS_LABELS[ncDetalhe.workflow_status] || ncDetalhe.workflow_status}
              </span>
            </div>

            <div style={s.detailBox}>
              <div style={s.detailLabel}>Descrição da NC</div>
              <div style={s.detailValue}>{ncDetalhe.descricao_nc || '—'}</div>
            </div>

            <div style={{ ...s.detailBox, ...s.row2 }}>
              <div>
                <div style={s.detailLabel}>Modo de análise</div>
                <div style={s.detailValue}>{ncDetalhe.modo_analise === 'investigacao' ? 'Investigação' : 'Registro'}</div>
              </div>
              <div>
                <div style={s.detailLabel}>Abertura</div>
                <div style={s.detailValue}>{formatDate(ncDetalhe.created_at)}</div>
              </div>
            </div>

            {ncDetalhe.causa_raiz_declaracao && (
              <div style={s.detailBox}>
                <div style={s.detailLabel}>Causa raiz identificada</div>
                <div style={s.detailValue}>{ncDetalhe.causa_raiz_declaracao}</div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
